/* eslint-disable */
// TODO: Descomentar quando o backend estiver em produção
import { ApolloError } from '@apollo/client'
import {
  IFooterConfig,
  IGeneralConfig,
  IMembersConfig,
  ISectionsConfig,
  ICustomBanners,
  ICustomProductList,
  ICustomBannersKind,
  IFeaturedProduct,
} from '@dtos/MembersConfig'
import { useGetMembersConfig } from '@services/api/graphql/membersConfig'
import { useToastController } from '@tamagui/toast'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const EXCLUDE_ERRORS = [
  'Object of type MembersConfig not found',
  'Token is invalid',
]

// Escola sem config e sessão vencida têm caminho próprio; não são falha.
export const isExpectedMembersConfigError = (error?: { message?: string }) =>
  !!error?.message && EXCLUDE_ERRORS.includes(error.message)

export const useMembersConfig = () => {
  const [membersConfigData, setMembersConfigData] = useState<IMembersConfig | null>(null)
  const toast = useToastController()
  const { t } = useTranslation('toast')
  const { getMembersConfig, refreshMembersConfig: queryMembersConfig, data, error, loading, called } =
    useGetMembersConfig({
      version: 'production',
    })

  // Falha propaga para quem chamou; o estado só muda com resposta em mãos.
  const refreshMembersConfig = useCallback(async () => {
    const { data } = await queryMembersConfig()
    setMembersConfigData(data?.membersConfig ?? null)
  }, [queryMembersConfig])

  useEffect(() => {
    try {
      const asyncGetMembersConfig = async () => {
        const { data } = await getMembersConfig()
        setMembersConfigData(data?.membersConfig)
      }
      if (!membersConfigData) {
        asyncGetMembersConfig()
      }
    } catch (error) {
      throw error
    }
  }, [getMembersConfig])

  useEffect(() => {
    if (error && !EXCLUDE_ERRORS.includes(error.message)) {
      toast.show(t('apolloErrors.membersConfig.title'), {
        message: t('genericRequestError.message'),
        type: 'error',
      })
    }
  }, [error, toast, t])

  // Respondeu de vez: com config (já no estado) ou sem nenhuma (o core devolve
  // `membersConfig: null` sem erro para escola sem config publicada).
  const isResolved =
    called &&
    !loading &&
    !!data &&
    (!!membersConfigData || !data.membersConfig)

  if (!data)
    return {
      data: null,
      error,
      loading,
      called,
      isResolved: false,
      refreshMembersConfig,
    }

  return {
    getMembersConfig,
    refreshMembersConfig,
    data: membersConfigData,
    error,
    loading,
    called,
    isResolved,
  } as {
    getMembersConfig: () => void
    refreshMembersConfig: () => Promise<void>
    data: IMembersConfig
    error: ApolloError
    loading: boolean
    called: boolean
    isResolved: boolean
  }
}



export const useGetFooterConfig = () => {
  const { data } = useMembersConfig()

  return { data: data?.footer } as { data: IFooterConfig | null }
}

export const useGetGeneralConfig = () => {
  const { data } = useMembersConfig()

  return { data: data?.general } as { data: IGeneralConfig | null }
}

export const useGetMyProductsConfig = () => {
  const { data } = useMembersConfig()

  const findMyProductsSection = data?.sections.find(
    (section) => section.kind === 'school_products',
  )
  return {
    data: findMyProductsSection,
  } as { data: ISectionsConfig | null }
}

export const useGetCustomBanners = () => {
  const { data } = useMembersConfig()

  const sortedCustomBanners = useMemo(() => {
    if (!data?.customBanners) return null

    const orderPriority: { [key in ICustomBannersKind]: number } = {
      duo: 1,
      trio: 2,
      single: 3,
    }

    return [...data.customBanners].sort(
      (a, b) => orderPriority[a.kind] - orderPriority[b.kind]
    )
  }, [data?.customBanners])

  return { data: sortedCustomBanners } as { data: ICustomBanners[] | null }
}

export const useGetCustomProductLists = () => {
  const { data } = useMembersConfig()

  return { data: data?.customProductLists } as { data: ICustomProductList[] | null }
}

export const useGetFeaturedProduct = () => {
  const { data, loading, called } = useMembersConfig()

  return { data: data?.featuredProduct ?? null, loading, called } as {
    data: IFeaturedProduct | null
    loading: boolean
    called: boolean
  }
}
